<template>
    <Dialog
        :value="true"
        :header-text="$t('Email settings')"
        persistent
        :width="email.integration_type === 'smtp' ? 800 : 700"
        @close_modal="$emit('close')"
    >
        <v-row>
            <v-col
                v-for="(field, index) in fields"
                :key="index"
                :cols="field.cols"
                class="pb-2"
                :class="field.type === 'switch' ? 'pt-3' : 'pt-0'"
            >
                <template v-if="!field.visible || (field.visible && field.visible())">
                    <text-field
                        v-if="field.type === 'text'"
                        :value="getFieldValue(field.field)"
                        :label="getFieldLabel(field)"
                        :tabindex="field.tabIndex"
                        :disabled="field.readonly || !canEdit"
                        :error-messages="getFieldError(field.field)"
                        @input="setFieldValue(field, $event)"
                    />
                    <text-field
                        v-else-if="field.type === 'number'"
                        :value="getFieldValue(field.field)"
                        :label="getFieldLabel(field)"
                        :disabled="!canEdit"
                        :tabindex="field.tabIndex"
                        :error-messages="getFieldError(field.field)"
                        @input="setFieldValue(field, $event)"
                    />
                    <password-field
                        v-else-if="field.type === 'password'"
                        :value="getFieldValue(field.field)"
                        :label="getFieldLabel(field)"
                        :disabled="!canEdit"
                        :tabindex="field.tabIndex"
                        :error-messages="getFieldError(field.field)"
                        @input="setFieldValue(field, $event)"
                    />
                    <switch-button
                        v-else-if="field.type === 'switch'"
                        :value="getFieldValue(field.field, true)"
                        :label="getFieldLabel(field)"
                        :disabled="!canEdit"
                        :tabindex="field.tabIndex"
                        :error-messages="getFieldError(field.field)"
                        @change="setFieldValue(field, $event)"
                    />
                    <dropdown
                        v-else-if="field.type === 'select'"
                        :selected="field.options.filter(x => x.value === getFieldValue(field.field))"
                        :label="getFieldLabel(field)"
                        :items="field.options"
                        :disabled="field.readonly || !canEdit"
                        :error-messages="getFieldError(field.field)"
                        name-key="name"
                        value-key="value"
                        show-selected-first
                        :tabindex="field.tabIndex"
                        @input="setFieldValue(field, $event.value)"
                    />
                </template>
            </v-col>
        </v-row>
        <template slot="footer">
            <danger-button
                v-if="canDelete"
                @click="$emit('delete')"
            >
                {{ $t("Delete") }}
            </danger-button>
            <v-spacer />
            <action-button
                v-if="isReconnectVisible"
                :icon="null"
                @click="$emit('reconnect', emailCopy)"
            >
                {{ $t('Reconnect') }}
            </action-button>
            <action-button
                v-if="isSendTestEmailVisible"
                :icon="null"
                @click="$emit('send-test-email', emailCopy)"
            >
                {{ $t('Send test email') }}
            </action-button>
            <primary-button @click="$emit('save', emailCopy)">
                {{ $t("Save") }}
            </primary-button>
        </template>
    </Dialog>
</template>

<script>
import Dialog from "../../Dialog.vue";
import { computed, reactive, ref } from "@vue/composition-api";
import { $t } from "@/i18n";
import { get } from '@/helpers/get-set'
import Loader from "@/utility/loader";
import AccountModule from "@/store/modules/account";
import UserModule from "@/store/modules/user";
import EmailIntegrationModule from "@/modules/emailIntegration/emailIntegration"
import RightsModule from "@/composables/use-rights";
import defaultSmtpSettings from "@/config/emailing/defaultSmtpSettings.json"

export default {
    name: "EditEmailAutomationDialog",
    components: {
        Dialog
    },
    props: {
        email: {
            type: Object,
            required: true
        },
        errors: {
            type: Array,
            default: null
        },
        companyId: {
            type: Number,
            required: true
        },
        disableVisibility: {
            type: Boolean,
            default: false
        },
        defaultForClient: {
            type: Boolean,
            default: false
        },
        hideDelete: {
            type: Boolean,
            default: false
        },
        hideReconnect: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const rightsModule = RightsModule()
        const { current_account } = AccountModule();
        const userModule = UserModule();
        const { can_edit_account } = RightsModule();
        const loader = reactive(new Loader())
        const emailCopy = ref(null);
        emailCopy.value = { ...props.email }

        const canEdit = computed(() => (props.email.owner_id === current_account.value.id || !props.email.owner_id) || can_edit_account(props.companyId))

        const onEmailServerSelect = (value) => {
            const selectedDefaultSmtpSettings = defaultSmtpSettings[value]

            if (selectedDefaultSmtpSettings) {
                emailCopy.value.smtp.host = selectedDefaultSmtpSettings.host
                emailCopy.value.smtp.port = selectedDefaultSmtpSettings.port
                emailCopy.value.smtp.security = selectedDefaultSmtpSettings.security
            } else {
                emailCopy.value.smtp.host = ''
                emailCopy.value.smtp.port = ''
                emailCopy.value.smtp.security = ''
            }
        }

        const fields = computed(() => {
            if (props.email.integration_type === 'smtp') {
                return [
                    {
                        title: $t('Email address or username'),
                        type: 'text',
                        field: 'email',
                        cols: 6,
                        required: true,
                        tabIndex: 1,
                    },
                    {
                        title: $t('Email server'),
                        type: 'select',
                        field: 'smtp.email_server',
                        options: [
                            {
                                name: $t('Other'),
                                value: 'other'
                            },
                            {
                                name: 'zone',
                                value: 'zone'
                            },
                            {
                                name: 'veebimajutus',
                                value: 'veebimajutus'
                            },
                        ],
                        cols: 3,
                        tabIndex: 7,
                        onSelect: onEmailServerSelect
                    },
                    {
                        title: $t('Password'),
                        type: 'password',
                        field: 'smtp.password',
                        cols: 6,
                        required: true,
                        tabIndex: 2,
                    },
                    {
                        title: $t('Host'),
                        type: 'text',
                        field: 'smtp.host',
                        cols: 6,
                        required: true,
                        tabIndex: 8,
                    },
                    {
                        title: $t('Sender name'),
                        type: 'text',
                        field: 'sender_name',
                        cols: 6,
                        required: true,
                        tabIndex: 3,
                    },
                    {
                        title: $t('Port'),
                        type: 'number',
                        field: 'smtp.port',
                        cols: 3,
                        required: true,
                        tabIndex: 9,
                    },
                    {
                        title: $t('Security'),
                        type: 'text',
                        field: 'smtp.security',
                        cols: 3,
                        required: true,
                        tabIndex: 10,
                    },
                    {
                        title: $t('Reply address'),
                        type: 'text',
                        field: 'reply_to_email',
                        cols: 6,
                        tabIndex: 4,
                    },
                    {
                        title: $t('Set default email server for company'),
                        type: 'switch',
                        field: 'smtp.default_smtp_server_for_company',
                        cols: 6,
                        tabIndex: 11,
                        visible: () => rightsModule.can_edit_account(props.companyId)
                    },
                    {
                        title: $t('Who can use?'),
                        type: 'select',
                        field: 'visibility',
                        readonly: props.disableVisibility,
                        options: EmailIntegrationModule.getEmailIntegrationVisibilities(userModule.getVisibleMembersByCompany(props.companyId), props.email.owner_id),
                        cols: 3,
                        required: true,
                        tabIndex: 5,
                    },
                    props.defaultForClient ? {
                        title: $t('Client default address'),
                        type: 'switch',
                        field: 'client_default_address',
                        cols: 12,
                        tabIndex: 6,
                    } : {
                        title: $t('My default email'),
                        type: 'switch',
                        field: 'default_address',
                        cols: 12,
                        tabIndex: 6,
                    }
                ]
            } else {
                return [
                    {
                        title: $t('Email address'),
                        type: 'text',
                        readonly: true,
                        field: 'email',
                        cols: 12,
                        required: true,
                        tabIndex: 0,
                    },
                    {
                        title: $t('Reply address'),
                        type: 'text',
                        field: 'reply_to_email',
                        cols: 12,
                        tabIndex: 1,
                    },
                    {
                        title: $t('Sender name'),
                        type: 'text',
                        field: 'sender_name',
                        cols: 12,
                        required: true,
                        tabIndex: 2,
                    },
                    {
                        title: $t('Who can use?'),
                        type: 'select',
                        field: 'visibility',
                        options: EmailIntegrationModule.getEmailIntegrationVisibilities(userModule.getVisibleMembersByCompany(props.companyId), props.email.owner_id),
                        cols: 6,
                        tabIndex: 3,
                        required: true,
                        readonly: props.disableVisibility,
                    },
                    props.defaultForClient ? {
                        title: $t('Client default address'),
                        type: 'switch',
                        field: 'client_default_address',
                        cols: 12,
                        tabIndex: 6,
                    } : {
                        title: $t('My default email'),
                        type: 'switch',
                        field: 'default_address',
                        cols: 12,
                        tabIndex: 6,
                    }
                ]
            }
        })

        const getFieldLabel = (field) => {
            let value = field.title
            if (field.required)  {
                value += ' *'
            }

            return value
        }

        const getFieldError = (name) => {
            if (!props.errors) {
                return null
            }

            let value = props.errors.find(x => {
                if (x.field === name) {
                    return true
                }

                let split = name.split('.')
                if (split.length > 1 && split[1] === x.field) {
                    return true
                }

                return false
            })
            if (!value) {
                return null
            }

            return value.error_message
        }

        const getFieldValue = (name, isSwitch = false) => {
            let value = get(emailCopy.value, name)

            if (isSwitch && !value) {
                return false
            }

            return value
        }

        const setFieldValue = (field, value) => {
            let split = field.field.split('.')
            if (split.length > 1) {
                emailCopy.value[split[0]][split[1]] = value
                ctx.emit('clear-error', split[1])
            } else {
                emailCopy.value[field.field] = value
                ctx.emit('clear-error', field.field)
            }

            if (field.onSelect) {
                field.onSelect(value)
            }
        }

        const isSendTestEmailVisible = computed(() => {
            return canEdit.value
        })

        const isReconnectVisible = computed(() => {
            return !props.hideReconnect && props.email.owner_id === current_account.value.id && props.email.integration_type !== 'smtp'
        })

        const canDelete = computed(() => !props.hideDelete && Boolean(props.email.id) && (can_edit_account(props.companyId) || props.email.owner_id === current_account.value.id))

        return {
            fields,
            getFieldValue,
            setFieldValue,
            emailCopy,
            canDelete,
            getFieldError,
            canEdit,
            getFieldLabel,
            isSendTestEmailVisible,
            isReconnectVisible
        }
    }
};
</script>